import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import { Z_PA_FAB } from '@/constants/zLayers';
import type { PaAvatarConfig } from '@easyflow/shared';
import { PaFace, type PaFaceState } from '../avatar/PaFace';

interface Props {
  color: string;
  AssistantIcon: React.ComponentType<{ className?: string }>;
  isStreaming: boolean;
  unreadCount?: number;
  onExpand: () => void;
  // Task #3041 — avatar personale: quando abilitato subentra all'icona e lo
  // stato del PA è comunicato dal layer di stato (niente spinner/sparkles).
  avatarEnabled?: boolean;
  avatarConfig?: PaAvatarConfig | null;
  avatarDefaultSeed?: string;
  faceState?: PaFaceState;
}

const STORAGE_KEY = 'pa-fab-position-v1';

interface Position { x: number; y: number }

function loadPosition(): Position | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Position>;
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') return { x: parsed.x, y: parsed.y };
  } catch { /* ignore */ }
  return null;
}

export const FloatingFab: React.FC<Props> = ({
  color, AssistantIcon, isStreaming, unreadCount = 0, onExpand,
  avatarEnabled = false, avatarConfig = null, avatarDefaultSeed = 'pa', faceState = 'idle',
}) => {
  const [pos, setPos] = useState<Position>(() => loadPosition() ?? { x: window.innerWidth - 88, y: window.innerHeight - 120 });
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const offsetRef = useRef<Position>({ x: 0, y: 0 });

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    draggingRef.current = true;
    movedRef.current = false;
    offsetRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      movedRef.current = true;
      const x = Math.max(8, Math.min(window.innerWidth - 64, e.clientX - offsetRef.current.x));
      const y = Math.max(8, Math.min(window.innerHeight - 64, e.clientY - offsetRef.current.y));
      setPos({ x, y });
    };
    const handleUp = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, []);

  const handleClick = () => {
    if (movedRef.current) return;
    onExpand();
  };

  // Portal su `document.body`: sgancia la bubble da wrapper con
  // `overflow`/transform che ne clipperebbero/riposizionerebbero il `fixed`.
  return createPortal(
    <button
      type="button"
      onMouseDown={onMouseDown}
      onClick={handleClick}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        backgroundColor: color,
        zIndex: Z_PA_FAB,
      }}
      className="h-14 w-14 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-105 transition-transform cursor-grab active:cursor-grabbing"
      title="Apri assistente"
    >
      {avatarEnabled ? (
        <PaFace
          avatarEnabled
          avatarConfig={avatarConfig}
          defaultSeed={avatarDefaultSeed}
          color={color}
          FallbackIcon={AssistantIcon}
          state={faceState}
          size={46}
        />
      ) : isStreaming
        ? <Loader2 className="h-5 w-5 animate-spin" />
        : <AssistantIcon className="h-5 w-5" />}
      {unreadCount > 0 && (avatarEnabled || !isStreaming) && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
      {!avatarEnabled && !unreadCount && !isStreaming && (
        <Sparkles className="absolute -bottom-1 -right-1 h-3 w-3 text-white/80" />
      )}
    </button>,
    document.body,
  );
};
